import type { ChatLogEntry } from '../types';

/**
 * Merge tool_call and tool_response entries by toolCallId.
 * When a tool_response arrives, it gets merged into the existing tool_call entry.
 */
export const mergeToolCalls = (entries: ChatLogEntry[]): ChatLogEntry[] => {
  const result: ChatLogEntry[] = [];
  const toolIndex = new Map<string, number>();

  for (const entry of entries) {
    if (
      (entry.kind === 'tool_call' || entry.kind === 'tool_response') &&
      entry.toolCallId
    ) {
      const existingIdx = toolIndex.get(entry.toolCallId);
      if (existingIdx === undefined) {
        toolIndex.set(entry.toolCallId, result.length);
        result.push({ ...entry, kind: 'tool_call' });
      } else {
        const existing = result[existingIdx];
        result[existingIdx] = {
          ...existing,
          args: entry.args || existing.args,
          response: entry.response || existing.response,
          status: entry.response ? 'complete' : existing.status,
          durationMs: entry.durationMs || existing.durationMs,
          agent: entry.agent || existing.agent,
          tool: entry.tool || existing.tool,
        };
      }
    } else {
      result.push(entry);
    }
  }

  return result;
};

/**
 * Group sub-agent traces by deepAgentRunId as children of a parent entry.
 * First entry with a given deepAgentRunId becomes the parent,
 * subsequent entries become its children.
 */
export const groupSubAgentLogs = (entries: ChatLogEntry[]): ChatLogEntry[] => {
  const result: ChatLogEntry[] = [];
  const parentIndex = new Map<string, number>();

  for (const entry of entries) {
    if (!entry.deepAgentRunId) {
      result.push(entry);
      continue;
    }

    const runId = entry.deepAgentRunId;
    const existingIdx = parentIndex.get(runId);
    if (existingIdx === undefined) {
      parentIndex.set(runId, result.length);
      result.push({ ...entry, children: [] });
    } else {
      const parent = result[existingIdx];
      result[existingIdx] = {
        ...parent,
        children: [...(parent.children || []), entry],
      };
    }
  }

  return result;
};

/**
 * Merge a tool response into an existing logs array by toolCallId.
 * Returns a new array with the merged entry.
 */
export const mergeToolResponse = (
  logs: ChatLogEntry[],
  toolCallId: string,
  response: string,
  durationMs?: number
): ChatLogEntry[] => {
  return logs.map((log) => {
    if (log.kind === 'tool_call' && log.toolCallId === toolCallId) {
      return {
        ...log,
        response,
        durationMs,
        status: 'complete' as const,
      };
    }
    return log;
  });
};
